import React, { useState, useMemo, forwardRef } from 'react';
import {
  TableCell,
  TableRow,
  Menu,
  MenuItem,
  TextField,
} from '@material-ui/core';
import { IconButton, Select } from '@coolremote/shared';
import { MoreVert, CheckOutlined, Edit, Check, Close } from '@material-ui/icons';
import clsx from 'clsx';
import { t } from 'ttag';
import { useConfirmation } from '@coolremote/shared';
import { useStoreActions } from '@models/RootStore';
import useStyles from './styles';

/**
 * UnitTableRow renders a unit-level row nested under a device row.
 * Shows unit-specific data: app name, mapping, control UI, universal, bind status.
 */
const UnitTableRow = forwardRef((props, ref) => {
  const classes = useStyles();

  const {
    bacnetUnit,
    handleParamTemplateAssign,
    handleParamTemplateUnassign,
    handleUiTemplateAssign,
    handleUiTemplateUnassign,
    handleConnectionChange,
    uiTemplates,
    goToUiTemplateMapping,
    goToParamTemplateMapping,
    onDeleteUnit,
    updateBacNetUnit,
    highlightRow,
    siteBacNetParameterTemplates,
    unitStatus,
    statusColors,
    statusLabels,
    onMenuClick,
  } = props;

  const [actionsMenuAnchorEl, setActionsMenuAnchorEl] = useState(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitleValue, setEditTitleValue] = useState('');
  const confirm = useConfirmation();
  const { addMessage } = useStoreActions((action) => action.errorMessage);
  const { deleteBacnetUnit } = useStoreActions((action) => action.bacnetDevices);

  const handleTitleEditStart = () => {
    setEditTitleValue(bacnetUnit.title ?? '');
    setIsEditingTitle(true);
  };

  const handleTitleSave = () => {
    updateBacNetUnit({
      id: bacnetUnit.id,
      data: { title: editTitleValue },
    });
    setIsEditingTitle(false);
  };

  const handleTitleCancel = () => {
    setEditTitleValue(bacnetUnit.title ?? '');
    setIsEditingTitle(false);
  };

  const filteredUiTemplates = useMemo(
    () => {
      if (!uiTemplates || !bacnetUnit) {
        return [];
      }
      return uiTemplates.filter((uiTemplate) => uiTemplate.paramTemplate == bacnetUnit.paramTemplate);
    },
    [uiTemplates, bacnetUnit]
  );

  const handleClick = (event) => {
    setActionsMenuAnchorEl(event.currentTarget);
    onMenuClick?.(bacnetUnit?.id);
  };

  const handleClose = () => {
    setActionsMenuAnchorEl(null);
  };

  const handleConnect = async () => {
    const result = await confirm({
      title: t`Connect Device?`,
      bodyMessage: t`Are you sure you want to connect this device?`,
      confirmText: t`Connect`,
      cancelText: t`Cancel`,
    });

    if (result) {
      handleConnectionChange(bacnetUnit?.id, true);
      handleClose();
    }
  };

  const handleDisconnect = async () => {
    const result = await confirm({
      title: t`Disconnect Device?`,
      bodyMessage: t`Are you sure you want to disconnect this device?`,
      confirmText: t`Disconnect`,
      cancelText: t`Cancel`,
    });

    if (result) {
      handleConnectionChange(bacnetUnit?.id, false);
      handleClose();
    }
  };

  const handleDeleteUnit = async () => {
    const result = await confirm({
      title: t`Delete Unit?`,
      bodyMessage: t`Are you sure you want to delete this unit?`,
      confirmText: t`Delete`,
      cancelText: t`Cancel`,
    });

    if (result) {
      await deleteBacnetUnit(bacnetUnit?.id);
      onDeleteUnit(bacnetUnit?.id);
      handleClose();
    }
  };

  const handleGoToParamTemplateMapping = () => {
    goToParamTemplateMapping(bacnetUnit.id, bacnetUnit.paramTemplate);
    handleClose();
  };

  const handleGoToUiTemplateMapping = () => {
    goToUiTemplateMapping(bacnetUnit.uiTemplate ?? null, bacnetUnit.id, bacnetUnit.paramTemplate);
    handleClose();
  };

  const isActionsMenuOpen = !!actionsMenuAnchorEl;

  const handleParamTemplateChange = async (templateId) => {
    try {
      if (templateId) {
        await handleParamTemplateAssign(templateId, bacnetUnit.id);
      } else {
        await handleParamTemplateUnassign(
          bacnetUnit?.paramTemplate,
          bacnetUnit.id
        );
      }
    } catch (error) {
      addMessage({ message: error.message, hideReportButton: true });
    }
  };

  const handleUiTemplateChange = async (templateId) => {
    try {
      if (templateId) {
        await handleUiTemplateAssign(templateId, bacnetUnit.id);
      } else {
        await handleUiTemplateUnassign(bacnetUnit?.uiTemplate, bacnetUnit.id);
      }
    } catch (error) {
      addMessage({ message: error.message, hideReportButton: true });
    }
  };

  if (!bacnetUnit) {
    return null;
  }

  const renderParamTemplateSelect = () => {
    if (!bacnetUnit.compatibleParamTemplates?.length) {
      return '--';
    }

    return (
      <Select
        menuPortal
        maxMenuWidth={400}
        value={bacnetUnit.paramTemplate || null}
        onChange={handleParamTemplateChange}
        disabled={bacnetUnit.isEngaged}
        classes={{ trigger: clsx(classes.inputRoot, classes.selectTrigger) }}
        options={[
          { value: null, label: '--', disabled: !bacnetUnit.paramTemplate },
          ...(bacnetUnit.compatibleParamTemplates?.map((template) => ({
            value: template.id,
            label: template.name,
          })) || [])
        ]}
      />
    );
  };

  const renderUiTemplatesSelect = () => {
    if (!filteredUiTemplates.length) {
      return '--';
    }

    return (
      <Select
        menuPortal
        maxMenuWidth={400} 
        value={bacnetUnit.uiTemplate || null}
        onChange={handleUiTemplateChange}
        disabled={bacnetUnit.isEngaged || !bacnetUnit.paramTemplate}
        classes={{ trigger: clsx(classes.inputRoot, classes.selectTrigger) }}
        options={[
          { value: null, label: '--', disabled: !bacnetUnit.uiTemplate },
          ...filteredUiTemplates.map((uiTemplate) => ({
            value: uiTemplate.id,
            label: uiTemplate.name,
          }))
        ]}
      />
    );
  };

  const getParamTemplateUniversalMapping = () => {
    const templateId = bacnetUnit?.paramTemplate;
    if (!templateId) {
      return false;
    }
    const template = siteBacNetParameterTemplates?.[templateId];
    return !!template?.entityTemplate;
  };

  const statusColor = statusColors?.[unitStatus] || '#545964';
  const statusLabel = statusLabels?.[unitStatus]?.() || '';

  return (
    <TableRow
      ref={ref}
      tabIndex={-1}
      className={clsx(classes.unitRow, { [classes.highlight]: highlightRow })}
    >
      {/* Empty cell for expand arrow column alignment */}
      <TableCell classes={{ root: classes.arrowPlaceholder }} />

      {/* Empty cells for device-level columns (Device Name, Vendor, Model, MAC, CA Device SN) */}
      <TableCell classes={{ root: classes.emptyCell }} />
      <TableCell classes={{ root: classes.emptyCell }} />
      <TableCell classes={{ root: classes.emptyCell }} />
      <TableCell classes={{ root: classes.emptyCell }} />
      <TableCell classes={{ root: classes.emptyCell }} />

      {/* App Unit - view/edit inline */}
      <TableCell classes={{ root: clsx(classes.TableCell, classes.appUnitNameCell) }} align="left">
        {isEditingTitle ? (
          <div className={classes.editContainer}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              variant="outlined"
              value={editTitleValue}
              onChange={(e) => setEditTitleValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleTitleSave();
                if (e.key === 'Escape') handleTitleCancel();
              }}
              InputProps={{ classes: { input: classes.editInput } }}
            />
            <IconButton size="small" onClick={handleTitleSave} className={classes.editAction}>
              <Check fontSize="small" style={{ color: '#5FBA82' }} />
            </IconButton>
            <IconButton size="small" onClick={handleTitleCancel} className={classes.editAction}>
              <Close fontSize="small" style={{ color: '#F05146' }} />
            </IconButton>
          </div>
        ) : (
          <div className={classes.viewContainer}>
            <span className={classes.titleText}>{bacnetUnit.title || '--'}</span>
            <IconButton size="small" onClick={handleTitleEditStart} className={classes.editIcon}>
              <Edit />
            </IconButton>
          </div>
        )}
      </TableCell>

      {/* Mapping */}
      <TableCell
        classes={{ root: clsx(classes.TableCell, classes.mediumWidth) }}
        align="left"
      >
        {renderParamTemplateSelect()}
      </TableCell>

      {/* Control UI */}
      <TableCell
        classes={{ root: clsx(classes.TableCell, classes.mediumWidth) }}
        align="left"
      >
        {renderUiTemplatesSelect()}
      </TableCell>

      {/* Universal */}
      <TableCell
        classes={{ root: clsx(classes.TableCell) }}
        align="center"
      >
        {getParamTemplateUniversalMapping() ? <CheckOutlined /> : null}
      </TableCell>

      {/* Status */}
      <TableCell
        classes={{ root: clsx(classes.TableCell, classes.statusCell) }}
        align="center"
        style={{ color: statusColor }}
      >
        {statusLabel}
      </TableCell>

      {/* Actions menu */}
      <TableCell classes={{ root: classes.TableCell }} align="left">
        <IconButton
          id="unit-actions-menu-button"
          aria-controls={isActionsMenuOpen ? 'unit-actions-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={isActionsMenuOpen ? 'true' : undefined}
          onClick={handleClick}
        >
          <MoreVert />
        </IconButton>
        <Menu
          id="unit-actions-menu"
          anchorEl={actionsMenuAnchorEl}
          open={isActionsMenuOpen}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'unit-actions-menu-button',
          }}
        >
          <MenuItem
            key="connect"
            disabled={bacnetUnit.isEngaged || !bacnetUnit.paramTemplate}
            onClick={handleConnect}
          >{t`Bind`}</MenuItem>
          <MenuItem
            key="disconnect"
            disabled={!bacnetUnit.isEngaged}
            onClick={handleDisconnect}
          >{t`Unbind`}</MenuItem>
          <MenuItem
            key="param-template-mapping"
            onClick={handleGoToParamTemplateMapping}
          >{t`Object Mapping`}</MenuItem>
          <MenuItem
            key="ui-template-mapping"
            onClick={handleGoToUiTemplateMapping}
          >{t`Control UI`}</MenuItem>
          <MenuItem disabled className={classes.menuItemWithLine}>
            <hr style={{ width: '100%', border: 'none', borderTop: '1px solid #ccc' }} />
          </MenuItem>
          <MenuItem
            key="delete"
            style={{ color: 'red' }}
            disabled={bacnetUnit.isEngaged}
            onClick={handleDeleteUnit}
          >{t`Delete App Unit`}</MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );
});

UnitTableRow.displayName = 'UnitTableRow';

export default UnitTableRow;
